// Global
import dynamic from 'next/dynamic';
import React from 'react';
import { tv } from 'tailwind-variants';

/**
 * Standardize SVG icons on a 48x48 grid to allow
 * for consistent use across the project
 *
 * Icon contents should be stored in the icons subdirectory
 * using the naming scheme 'icon--[name].tsx'
 */

export type IconTypes =
  | undefined
  | 'arrow-left'
  | 'arrow-right'
  | 'chevron-down'
  | 'chevron-up'
  | 'chevron-right'
  | 'chevron-left'
  | 'external-link'
  | 'search'
  | 'info'
  | 'notification'
  | 'minus'
  | 'hospital'
  | 'calendar-blank'
  | 'calendar-check'
  | 'user-plus'
  | 'close-circle'
  | 'close'
  | 'map-pin'
  | 'chat'
  | 'funnel'
  | 'check'
  | 'check-circle'
  | 'xcircle'
  | 'star'
  | 'star-half'
  | 'star-outline'
  | 'gpsfix'
  | 'user'
  | 'magnifier'
  | 'calendar-heart'
  | 'hand-heart'
  | 'user-three'
  | 'chat-circle-heart'
  | 'video-camera'
  | 'custom-clock'
  | 'custom-sling'
  | 'custom-provider'
  | 'folder-open'
  | 'gift'
  | 'phone'
  | 'device-mobile'
  | 'stethoscope'
  | 'menu-close'
  | 'megaphone'
  | 'download'
  | 'facebook'
  | 'folder-simple-plus'
  | 'hamburger-menu'
  | 'instagram'
  | 'magnifier'
  | 'new-tab'
  | 'new-window'
  | 'outline-search'
  | 'pause'
  | 'pinterest'
  | 'play'
  | 'image-play'
  | 'plus'
  | 'refine'
  | 'sorting'
  | 'tiktok'
  | 'youtube'
  | 'bell'
  | 'twitter'
  | 'linkedin'
  | 'print'
  | 'clock-bold';

export type SvgIconSize = 'xxs' | 'xs' | 'sm' | 'md' | 'em' | 'lg' | 'xl';

export type SVGFill = 'currentColor' | 'none';

export interface SvgIconProps {
  className?: string;
  fill?: SVGFill; // The "fill" attribute must be applied to individual <path /> tags in order to be effective. Applying it to <svg /> does nothing.
  icon: IconTypes;
  size?: SvgIconSize;
  viewBox?: string; // This could pretty easily be hard coded as the "size" attribute is doing the hard work here.
  title?: string;
  ariaLabel?: string;
  ariaHidden?: boolean;
}

const SvgIcon = ({
  className,
  fill = 'currentColor',
  icon,
  size = 'sm',
  viewBox = '0 0 24 24',
  title,
  ariaLabel,
  ariaHidden,
}: SvgIconProps): JSX.Element => {
  if (!icon) return <></>;
  const IconContent = dynamic(() => import(`./icons/icon--${icon}`));

  return (
    <svg
      className={TAILWIND_VARIANTS({ className, size })}
      fill={fill}
      viewBox={viewBox}
      data-icon={icon}
      xmlns="http://www.w3.org/2000/svg"
      aria-label={ariaLabel}
      aria-hidden={ariaHidden}
    >
      {title && <title>{title}</title>}
      <IconContent />
    </svg>
  );
};

export default React.memo(SvgIcon);

const TAILWIND_VARIANTS = tv({
  base: [],
  variants: {
    size: {
      xxs: ['!h-3', '!w-3'], //added solely for fixing the search and results UI
      xs: ['!h-4', '!w-4'],
      sm: ['!h-8', '!w-8'],
      md: ['!h-10', '!w-10'],
      lg: ['!h-12', '!w-12'],
      xl: ['!h-20', '!w-20'],
      em: ['!h-em', '!w-em'],
    },
  },
});
